import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Unsubscribe,
  serverTimestamp,
  increment,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { attendeeDisplayName } from '../utils/attendeeDisplayName';
import { createPrize, awardPrize } from './prizes';
import type { PrizeType } from './prizes';
import type { TriviaClue } from './triviaClues';

export type TriviaRuleSet = 'first_correct' | 'most_correct' | 'sudden_death';
export type TriviaGameStatus = 'setup' | 'review' | 'active' | 'between_questions' | 'revealing' | 'completed' | 'cancelled';

export interface TriviaGame {
  id: string;
  eventId: string;
  djId: string;
  status: TriviaGameStatus;
  ruleSet: TriviaRuleSet;
  reviewDurationSeconds: number;
  questionTimeSeconds: number;
  prizeType: PrizeType;
  prizeCustomText?: string;
  prizeId?: string;
  totalQuestions: number;
  currentQuestionIndex: number;
  currentQuestionStartedAt?: string;
  revealedCorrectSongId?: string;
  currentChoices?: string[];
  currentClueText?: string;
  playerCount: number;
  activePlayers: number;
  firstCorrectWinnerId?: string;
  firstCorrectWinnerNickname?: string;
  questionCount: number;
  winnerIds: string[];
  winnerNicknames: string[];
  createdAt: string;
  updatedAt: string;
}

export interface TriviaAnswerKey {
  eventId: string;
  clueIds: string[];
  currentQuestionIndex: number;
  currentCorrectSongId: string | null;
}

export interface TriviaAnswer {
  id: string;
  gameId: string;
  eventId: string;
  attendeeId: string;
  nickname?: string;
  questionIndex: number;
  selectedSongId: string;
  correct: boolean;
  answeredAt: string;
  createdAt: string;
}

export interface TriviaPlayerState {
  id: string;
  gameId: string;
  eventId: string;
  attendeeId: string;
  nickname?: string;
  joined: boolean;
  eliminated: boolean;
  eliminatedAtQuestion?: number;
  correctCount: number;
  cumulativeTimeMs: number;
  createdAt: string;
  updatedAt: string;
}

function docToTriviaGame(docSnap: any): TriviaGame {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    eventId: data.eventId,
    djId: data.djId,
    status: data.status,
    ruleSet: data.ruleSet,
    reviewDurationSeconds: data.reviewDurationSeconds || 20,
    questionTimeSeconds: data.questionTimeSeconds || 15,
    prizeType: data.prizeType,
    prizeCustomText: data.prizeCustomText || undefined,
    prizeId: data.prizeId || undefined,
    totalQuestions: data.totalQuestions ?? 0,
    currentQuestionIndex: data.currentQuestionIndex ?? -1,
    currentQuestionStartedAt: data.currentQuestionStartedAt?.toDate?.()?.toISOString?.() || data.currentQuestionStartedAt || undefined,
    revealedCorrectSongId: data.revealedCorrectSongId || undefined,
    currentChoices: data.currentChoices || undefined,
    currentClueText: data.currentClueText || undefined,
    playerCount: data.playerCount || 0,
    activePlayers: data.activePlayers || 0,
    firstCorrectWinnerId: data.firstCorrectWinnerId || undefined,
    firstCorrectWinnerNickname: data.firstCorrectWinnerNickname || undefined,
    questionCount: data.questionCount || 0,
    winnerIds: data.winnerIds || [],
    winnerNicknames: data.winnerNicknames || [],
    createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt || '',
    updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() || data.updatedAt || '',
  };
}

function docToPlayerState(docSnap: any): TriviaPlayerState {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    gameId: data.gameId,
    eventId: data.eventId,
    attendeeId: data.attendeeId,
    nickname: data.nickname || undefined,
    joined: data.joined || false,
    eliminated: data.eliminated || false,
    eliminatedAtQuestion: data.eliminatedAtQuestion ?? undefined,
    correctCount: data.correctCount || 0,
    cumulativeTimeMs: data.cumulativeTimeMs || 0,
    createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt || '',
    updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() || data.updatedAt || '',
  };
}

function answerKeyRef(gameId: string) {
  return doc(db, 'triviaGames', gameId, 'private', 'answer');
}

function docToAnswerKey(docSnap: any): TriviaAnswerKey {
  const data = docSnap.data();
  return {
    eventId: data.eventId,
    clueIds: data.clueIds || [],
    currentQuestionIndex: data.currentQuestionIndex ?? -1,
    currentCorrectSongId: data.currentCorrectSongId || null,
  };
}

async function getAnswerKey(gameId: string): Promise<TriviaAnswerKey> {
  const answerKeySnap = await getDoc(answerKeyRef(gameId));
  if (!answerKeySnap.exists()) throw new Error('Answer key not found');
  return docToAnswerKey(answerKeySnap);
}

// --- Game Lifecycle ---

export async function createTriviaGame(
  eventId: string,
  djId: string,
  config: {
    ruleSet: TriviaRuleSet;
    reviewDurationSeconds?: number;
    questionTimeSeconds?: number;
    prizeType: PrizeType;
    prizeCustomText?: string;
    clueIds: string[];
  }
): Promise<string> {
  const gameRef = doc(collection(db, 'triviaGames'));
  const gameId = gameRef.id;

  const defaultQuestionTime = config.ruleSet === 'sudden_death' ? 5 : 15;

  const batch = writeBatch(db);
  batch.set(gameRef, {
    eventId,
    djId,
    status: 'setup',
    ruleSet: config.ruleSet,
    reviewDurationSeconds: config.reviewDurationSeconds || 20,
    questionTimeSeconds: config.questionTimeSeconds || defaultQuestionTime,
    prizeType: config.prizeType,
    prizeCustomText: config.prizeCustomText || null,
    prizeId: null,
    totalQuestions: config.clueIds.length,
    currentQuestionIndex: -1,
    currentQuestionStartedAt: null,
    revealedCorrectSongId: null,
    currentChoices: null,
    currentClueText: null,
    playerCount: 0,
    activePlayers: 0,
    firstCorrectWinnerId: null,
    firstCorrectWinnerNickname: null,
    questionCount: 0,
    winnerIds: [],
    winnerNicknames: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  batch.set(answerKeyRef(gameId), {
    eventId,
    clueIds: config.clueIds,
    currentQuestionIndex: -1,
    currentCorrectSongId: null,
  });
  await batch.commit();

  await updateDoc(doc(db, 'events', eventId), {
    'settings.triviaGameActive': true,
    'settings.triviaGameId': gameId,
    updatedAt: serverTimestamp(),
  });

  return gameId;
}

export async function startGame(gameId: string): Promise<void> {
  const gameRef = doc(db, 'triviaGames', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const game = docToTriviaGame(gameSnap);

  if (game.ruleSet === 'sudden_death') {
    await updateDoc(gameRef, {
      status: 'review',
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(gameRef, {
      status: 'active',
      updatedAt: serverTimestamp(),
    });
    await pushNextQuestion(gameId);
  }
}

export async function pushNextQuestion(gameId: string): Promise<void> {
  const gameRef = doc(db, 'triviaGames', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const game = docToTriviaGame(gameSnap);
  const { clueIds } = await getAnswerKey(gameId);
  const nextIndex = game.currentQuestionIndex + 1;

  if (nextIndex >= clueIds.length) {
    throw new Error('No more questions available');
  }

  const clueId = clueIds[nextIndex];
  const clueSnap = await getDoc(doc(db, 'triviaClues', clueId));
  if (!clueSnap.exists()) throw new Error('Clue not found');

  const clue = clueSnap.data() as TriviaClue;
  const correctSongId = clue.songRequestId;

  const allSongIds = await getAllSongIdsFromClues(clueIds);
  const distractors = pickDistractors(allSongIds, correctSongId, 3);
  const choices = [correctSongId, ...distractors].sort();

  const batch = writeBatch(db);
  batch.update(gameRef, {
    currentQuestionIndex: nextIndex,
    currentChoices: choices,
    currentClueText: clue.clueText,
    currentQuestionStartedAt: serverTimestamp(),
    revealedCorrectSongId: null,
    questionCount: nextIndex + 1,
    status: 'active',
    firstCorrectWinnerId: game.ruleSet === 'first_correct' ? null : (game.firstCorrectWinnerId || null),
    firstCorrectWinnerNickname: game.ruleSet === 'first_correct' ? null : (game.firstCorrectWinnerNickname || null),
    updatedAt: serverTimestamp(),
  });
  batch.update(answerKeyRef(gameId), {
    currentQuestionIndex: nextIndex,
    currentCorrectSongId: correctSongId,
  });
  await batch.commit();
}

async function getAllSongIdsFromClues(clueIds: string[]): Promise<string[]> {
  const songIds: string[] = [];
  for (const clueId of clueIds) {
    const clueSnap = await getDoc(doc(db, 'triviaClues', clueId));
    if (clueSnap.exists()) {
      songIds.push(clueSnap.data().songRequestId);
    }
  }
  return [...new Set(songIds)];
}

function pickDistractors(allSongIds: string[], correctId: string, count: number): string[] {
  const available = allSongIds.filter(id => id !== correctId);
  const result: string[] = [];
  const used = new Set<string>();

  while (result.length < count && result.length < available.length) {
    const randomValues = new Uint32Array(1);
    crypto.getRandomValues(randomValues);
    const idx = randomValues[0] % available.length;
    if (!used.has(available[idx])) {
      used.add(available[idx]);
      result.push(available[idx]);
    }
  }

  return result;
}

export async function endCurrentQuestion(gameId: string): Promise<void> {
  const gameRef = doc(db, 'triviaGames', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const game = docToTriviaGame(gameSnap);

  if (game.ruleSet === 'sudden_death') {
    const playersQuery = query(
      collection(db, 'triviaPlayerStates'),
      where('gameId', '==', gameId),
      where('eliminated', '==', false)
    );
    const activePlayers = await getDocs(playersQuery);
    let eliminatedCount = 0;

    for (const playerDoc of activePlayers.docs) {
      const answerId = `${gameId}_${playerDoc.data().attendeeId}_${game.currentQuestionIndex}`;
      const answerSnap = await getDoc(doc(db, 'triviaAnswers', answerId));
      if (!answerSnap.exists()) {
        await updateDoc(doc(db, 'triviaPlayerStates', playerDoc.id), {
          eliminated: true,
          eliminatedAtQuestion: game.currentQuestionIndex,
          updatedAt: serverTimestamp(),
        });
        eliminatedCount++;
      }
    }

    if (eliminatedCount > 0) {
      await updateDoc(gameRef, {
        activePlayers: increment(-eliminatedCount),
        updatedAt: serverTimestamp(),
      });
    }
  }

  const answerKey = await getAnswerKey(gameId);
  await updateDoc(gameRef, {
    status: 'between_questions',
    revealedCorrectSongId: answerKey.currentCorrectSongId,
    updatedAt: serverTimestamp(),
  });
}

export async function revealGame(gameId: string): Promise<void> {
  const gameRef = doc(db, 'triviaGames', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const game = docToTriviaGame(gameSnap);
  let winnerIds: string[] = [];
  let winnerNicknames: string[] = [];

  if (game.ruleSet === 'first_correct' && game.firstCorrectWinnerId) {
    winnerIds = [game.firstCorrectWinnerId];
    winnerNicknames = [attendeeDisplayName(game.firstCorrectWinnerNickname, game.firstCorrectWinnerId)];
  } else if (game.ruleSet === 'most_correct') {
    const leaderboard = await getLeaderboard(gameId);
    if (leaderboard.length > 0) {
      winnerIds = [leaderboard[0].attendeeId];
      winnerNicknames = [attendeeDisplayName(leaderboard[0].nickname, leaderboard[0].attendeeId)];
    }
  } else if (game.ruleSet === 'sudden_death') {
    const activeQuery = query(
      collection(db, 'triviaPlayerStates'),
      where('gameId', '==', gameId),
      where('eliminated', '==', false)
    );
    const activePlayers = await getDocs(activeQuery);

    if (activePlayers.size === 1) {
      const winner = activePlayers.docs[0].data();
      winnerIds = [winner.attendeeId];
      winnerNicknames = [attendeeDisplayName(winner.nickname, winner.attendeeId)];
    } else if (activePlayers.size > 1) {
      const sorted = activePlayers.docs
        .map(d => d.data())
        .sort((a, b) => a.cumulativeTimeMs - b.cumulativeTimeMs);
      winnerIds = [sorted[0].attendeeId];
      winnerNicknames = [attendeeDisplayName(sorted[0].nickname, sorted[0].attendeeId)];
    }
  }

  let prizeId: string | undefined;
  if (winnerIds.length > 0) {
    prizeId = await createPrize(game.eventId, game.djId, game.prizeType, {
      customText: game.prizeCustomText,
      gameSource: 'trivia',
      gameRoundId: `trivia-${gameId}`,
    });
    await awardPrize(prizeId, winnerIds[0], winnerNicknames[0]);
  }

  await updateDoc(gameRef, {
    status: 'revealing',
    winnerIds,
    winnerNicknames,
    prizeId: prizeId || null,
    updatedAt: serverTimestamp(),
  });
}

export async function completeGame(gameId: string): Promise<void> {
  const gameRef = doc(db, 'triviaGames', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const game = docToTriviaGame(gameSnap);

  await updateDoc(gameRef, {
    status: 'completed',
    updatedAt: serverTimestamp(),
  });

  await updateDoc(doc(db, 'events', game.eventId), {
    'settings.triviaGameActive': false,
    'settings.triviaGameId': '',
    updatedAt: serverTimestamp(),
  });
}

export async function cancelGame(gameId: string): Promise<void> {
  const gameRef = doc(db, 'triviaGames', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const game = docToTriviaGame(gameSnap);

  await updateDoc(gameRef, {
    status: 'cancelled',
    updatedAt: serverTimestamp(),
  });

  await updateDoc(doc(db, 'events', game.eventId), {
    'settings.triviaGameActive': false,
    'settings.triviaGameId': '',
    updatedAt: serverTimestamp(),
  });
}

// --- Player Actions ---

export async function joinGame(
  gameId: string,
  eventId: string,
  attendeeId: string,
  nickname?: string
): Promise<void> {
  const stateId = `${gameId}_${attendeeId}`;
  const stateRef = doc(db, 'triviaPlayerStates', stateId);
  const gameRef = doc(db, 'triviaGames', gameId);

  const batch = writeBatch(db);
  batch.set(stateRef, {
    gameId,
    eventId,
    attendeeId,
    nickname: nickname || null,
    joined: true,
    eliminated: false,
    eliminatedAtQuestion: null,
    correctCount: 0,
    cumulativeTimeMs: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  batch.update(gameRef, {
    playerCount: increment(1),
    activePlayers: increment(1),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

export async function submitAnswer(
  gameId: string,
  questionIndex: number,
  selectedSongId: string
): Promise<{ correct: boolean; eliminated?: boolean }> {
  const callable = httpsCallable<
    { gameId: string; questionIndex: number; selectedSongId: string },
    { correct: boolean; eliminated: boolean }
  >(functions, 'submitTriviaAnswer');
  const { data } = await callable({ gameId, questionIndex, selectedSongId });
  return { correct: data.correct, eliminated: data.eliminated || undefined };
}

// --- Queries ---

export function subscribeToTriviaGame(eventId: string, callback: (game: TriviaGame | null) => void): Unsubscribe {
  const q = query(
    collection(db, 'triviaGames'),
    where('eventId', '==', eventId),
    where('status', 'in', ['setup', 'review', 'active', 'between_questions', 'revealing'])
  );
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(null);
      return;
    }
    callback(docToTriviaGame(snapshot.docs[0]));
  });
}

export function subscribeToMyPlayerState(
  gameId: string,
  attendeeId: string,
  callback: (state: TriviaPlayerState | null) => void
): Unsubscribe {
  const stateId = `${gameId}_${attendeeId}`;
  return onSnapshot(doc(db, 'triviaPlayerStates', stateId), (docSnap) => {
    if (!docSnap.exists()) {
      callback(null);
      return;
    }
    callback(docToPlayerState(docSnap));
  });
}

// DJ only: attendees can't read the answer key
export function subscribeToTriviaAnswerKey(
  gameId: string,
  callback: (answerKey: TriviaAnswerKey | null) => void
): Unsubscribe {
  return onSnapshot(answerKeyRef(gameId), (docSnap) => {
    if (!docSnap.exists()) {
      callback(null);
      return;
    }
    callback(docToAnswerKey(docSnap));
  }, (error) => {
    console.error('[trivia] Answer key listener failed:', error);
    callback(null);
  });
}

export async function getLeaderboard(gameId: string): Promise<TriviaPlayerState[]> {
  const q = query(
    collection(db, 'triviaPlayerStates'),
    where('gameId', '==', gameId),
    orderBy('correctCount', 'desc'),
    orderBy('cumulativeTimeMs', 'asc'),
    limit(10)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToPlayerState);
}

export async function getGameHistory(eventId: string): Promise<TriviaGame[]> {
  const q = query(
    collection(db, 'triviaGames'),
    where('eventId', '==', eventId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToTriviaGame);
}

// --- Utilities ---

export function seededShuffle(items: string[], seed: string): string[] {
  const arr = [...items];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }

  for (let i = arr.length - 1; i > 0; i--) {
    hash = ((hash << 5) - hash + i) | 0;
    const j = Math.abs(hash) % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}
